/**
 * Prices API
 * Historical prices and price forecasting
 */

import { apiClient } from "./client";
import type {
  EnergyPriceResponse,
  ForecastedLucrativeHour,
  ForecastEvaluateResponse,
  ForecastGenerateResponse,
  Granularity,
  LucrativeHour,
  PriceForecastMatrix,
  PriceForecastStatistics,
  PriceMatrix,
  PriceStatistics,
} from "./types";

// ============================================================================
// HISTORICAL PRICES
// ============================================================================

/**
 * Get historical prices from GME
 */
export async function getPriceHistory(params: {
  startDate?: string;
  endDate?: string;
  zone?: string;
  market?: string;
}): Promise<EnergyPriceResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.market) searchParams.append("market", params.market);

  const query = searchParams.toString();
  return apiClient.get<EnergyPriceResponse[]>(
    `/api/v1/prices/history${query ? `?${query}` : ""}`
  );
}

/**
 * Get price matrix for Production Planning view
 * @param params.granularity PT60 for hourly (24 columns) or PT15 for quarter-hourly (96 columns)
 */
export async function getPriceMatrix(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  granularity?: Granularity;
}): Promise<PriceMatrix> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<PriceMatrix>(
    `/api/v1/prices/matrix${query ? `?${query}` : ""}`
  );
}

/**
 * Get most lucrative hours (highest prices)
 */
export async function getLucrativeHours(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  topN?: number;
}): Promise<LucrativeHour[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.topN) searchParams.append("top_n", params.topN.toString());

  const query = searchParams.toString();
  return apiClient.get<LucrativeHour[]>(
    `/api/v1/prices/lucrative-hours${query ? `?${query}` : ""}`
  );
}

/**
 * Get price statistics for a date range
 */
export async function getPriceStatistics(params: {
  startDate?: string;
  endDate?: string;
  zone?: string;
}): Promise<PriceStatistics> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.zone) searchParams.append("zone", params.zone);

  const query = searchParams.toString();
  return apiClient.get<PriceStatistics>(
    `/api/v1/prices/statistics${query ? `?${query}` : ""}`
  );
}

// ============================================================================
// PRICE FORECASTS
// ============================================================================

/**
 * Generate new price forecast using Prophet model
 */
export async function generatePriceForecast(params: {
  zone?: string;
  daysAhead?: number;
}): Promise<ForecastGenerateResponse> {
  const searchParams = new URLSearchParams();
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.daysAhead) searchParams.append("days_ahead", params.daysAhead.toString());

  const query = searchParams.toString();
  return apiClient.post<ForecastGenerateResponse>(
    `/api/v1/prices/forecast/generate${query ? `?${query}` : ""}`,
    {}
  );
}

/**
 * Get forecasted price matrix for Production Planning
 * @param params.granularity PT60 for hourly (24 columns) or PT15 for quarter-hourly (96 columns)
 */
export async function getPriceForecastMatrix(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  granularity?: Granularity;
}): Promise<PriceForecastMatrix> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<PriceForecastMatrix>(
    `/api/v1/prices/forecast/matrix${query ? `?${query}` : ""}`
  );
}

/**
 * Get most lucrative forecasted hours
 */
export async function getForecastedLucrativeHours(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  topN?: number;
}): Promise<ForecastedLucrativeHour[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.topN) searchParams.append("top_n", params.topN.toString());

  const query = searchParams.toString();
  return apiClient.get<ForecastedLucrativeHour[]>(
    `/api/v1/prices/forecast/lucrative-hours${query ? `?${query}` : ""}`
  );
}

/**
 * Get price forecast statistics
 */
export async function getPriceForecastStatistics(params: {
  startDate?: string;
  endDate?: string;
  zone?: string;
}): Promise<PriceForecastStatistics> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.zone) searchParams.append("zone", params.zone);

  const query = searchParams.toString();
  return apiClient.get<PriceForecastStatistics>(
    `/api/v1/prices/forecast/statistics${query ? `?${query}` : ""}`
  );
}

/**
 * Evaluate forecast model quality
 */
export async function evaluateForecastModel(params: {
  zone?: string;
  daysBack?: number;
  testDays?: number;
}): Promise<ForecastEvaluateResponse> {
  const searchParams = new URLSearchParams();
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.daysBack) searchParams.append("days_back", params.daysBack.toString());
  if (params.testDays) searchParams.append("test_days", params.testDays.toString());

  const query = searchParams.toString();
  return apiClient.get<ForecastEvaluateResponse>(
    `/api/v1/prices/forecast/evaluate${query ? `?${query}` : ""}`
  );
}

/**
 * Get 5 most expensive consecutive hours per day for Production Planning
 * This helps identify the best times to sell/discharge battery
 */
export async function getConsecutiveExpensiveHours(params: {
  startDate?: string;
  days?: number;
  zone?: string;
}): Promise<Map<string, number[]>> {
  const matrix = await getPriceForecastMatrix(params);
  const result = new Map<string, number[]>();

  for (const row of matrix.rows) {
    // Find 5 consecutive hours with highest total price
    let maxSum = -Infinity;
    let bestStart = 0;

    for (let start = 0; start <= 19; start++) {
      // 24 - 5 = 19 possible start positions
      let sum = 0;
      for (let i = 0; i < 5; i++) {
        sum += row.hours[start + i] ?? 0;
      }
      if (sum > maxSum) {
        maxSum = sum;
        bestStart = start;
      }
    }

    // Store the 5 consecutive hours
    result.set(row.date, [
      bestStart,
      bestStart + 1,
      bestStart + 2,
      bestStart + 3,
      bestStart + 4,
    ]);
  }

  return result;
}
